<template>
  <NavBar v-if="!$route.meta.hideNavbar" />

  <main
    class="qp-app__main"
    :class="{
      'qp-app__main--iframe': $route.meta.allowIframe,
      'qp-app__main--no-nav': $route.meta.hideNavbar,
      'qp-app__main--fill': $route.meta.fillViewport,
    }"
  >
    <!-- Страница пересоздаётся при смене адреса. meta.keepOnQueryChange —
         для страниц, которые держат фильтры в query и обновляют выдачу сами:
         иначе каждый клик по фильтру сбрасывал бы их состояние и фокус. -->
    <router-view
      v-slot="{ Component }"
      :key="$route.meta.keepOnQueryChange ? $route.path : $route.fullPath"
    >
      <transition name="qp-route" mode="out-in">
        <component :is="Component" />
      </transition>
    </router-view>
  </main>

  <!-- meta.hideFooter — для рабочих страниц на всю высоту окна, где футер лишний. -->
  <BasicFooter v-if="!$route.meta.allowIframe && !$route.meta.hideFooter" />
  <MobileBottomBar v-if="!$route.meta.allowIframe && isMobile" />

  <ToastMessage ref="toast" />

  <Transition name="qp-fade">
    <button
      v-if="showScrollTop && !$route.meta.allowIframe"
      type="button"
      class="qp-app__scroll-top"
      aria-label="Scroll to top"
      @click="scrollToTop"
    >
      <svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true">
        <path d="M3 10l5-5 5 5" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" />
      </svg>
    </button>
  </Transition>
</template>

<script>
import { provide, ref, onMounted, onUnmounted } from 'vue';
import NavBar from './components/common/NavBar.vue';
import BasicFooter from './components/common/BasicFooter.vue';
import MobileBottomBar from './components/common/MobileBottomBar.vue';
import ToastMessage from './components/common/ToastMessage.vue';
import { useTitle } from '@/composables/useTitle';
import { initAccent } from '@/composables/useAccent';

export default {
  name: 'App',
  components: { NavBar, BasicFooter, MobileBottomBar, ToastMessage },
  setup() {
    const toast = ref(null);
    const toastApi = { show: (msg, type) => toast.value?.show(msg, type) };
    provide('toast', toastApi);
    if (typeof window !== 'undefined') window.appToast = toastApi;

    useTitle();
    initAccent();

    const isMobile = ref(false);
    const showScrollTop = ref(false);
    const onScroll = () => { showScrollTop.value = window.scrollY > 360; };
    const updateIsMobile = () => { isMobile.value = window.innerWidth <= 768; };
    onMounted(() => { 
      window.addEventListener('scroll', onScroll, { passive: true });
      updateIsMobile();
      window.addEventListener('resize', updateIsMobile);
    });
    onUnmounted(() => { 
      window.removeEventListener('scroll', onScroll); 
      window.removeEventListener('resize', updateIsMobile);
    });

    const scrollToTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });

    return { toast, showScrollTop, scrollToTop, isMobile };
  },
};
</script>

<style>
.qp-app__main {
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  min-width: 0;
  padding-block-start: var(--layout-navbar-h);
  background: var(--surface-canvas);
}

.qp-app__main--no-nav {
  padding-block-start: 0;
}

.qp-app__main--iframe {
  padding: 0;
}

@media (max-width: 560px) {
  .qp-app__main { padding-block-start: var(--layout-navbar-h-mobile); }
  .qp-app__main--no-nav { padding-block-start: 0; }
}

@media (max-width: 1024px) {
  .qp-app__main { padding-block-end: 80px; }
  .qp-app__main--iframe { padding-block-end: 0; }
  /* meta.fillViewport: страница сама занимает окно под навбаром и сама
     отступает от нижней панели — запас под панель ей не нужен. */
  .qp-app__main--fill { padding-block-end: 0; }
}

/* --- Scroll-to-top — tiny, hairline, no halo --- */

.qp-app__scroll-top {
  position: fixed;
  right: var(--space-lg);
  bottom: var(--space-lg);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  color: var(--ink);
  background: var(--surface-base);
  border: 1px solid var(--hairline-strong);
  border-radius: var(--radius-pill);
  cursor: pointer;
  z-index: var(--z-sticky);
  transition:
    border-color var(--dur-xs) var(--ease-out-quart),
    background-color var(--dur-xs) var(--ease-out-quart),
    transform var(--dur-sm) var(--ease-out-expo);
}

.qp-app__scroll-top:hover {
  border-color: var(--accent);
  color: var(--accent);
  transform: translateY(-2px);
}

.qp-app__scroll-top:focus-visible {
  outline: 0;
  box-shadow: var(--shadow-focus);
}

@media (max-width: 1024px) {
  .qp-app__scroll-top { bottom: calc(80px + var(--space-sm)); right: var(--space-sm); }
}

.qp-fade-enter-active,
.qp-fade-leave-active { transition: opacity var(--dur-sm) var(--ease-out-quart), transform var(--dur-sm) var(--ease-out-quart); }
.qp-fade-enter-from,
.qp-fade-leave-to { opacity: 0; transform: translateY(6px); }

/* --- Route change: quick mask-free reveal --- */

.qp-route-enter-active {
  transition:
    opacity 220ms var(--ease-out-expo),
    transform 280ms var(--ease-out-expo),
    filter 280ms var(--ease-out-expo);
}

.qp-route-leave-active {
  transition:
    opacity 140ms var(--ease-out-quart),
    transform 180ms var(--ease-out-quart),
    filter 180ms var(--ease-out-quart);
}

.qp-route-enter-from {
  opacity: 0;
  transform: translateY(8px);
  filter: blur(2px);
}

.qp-route-leave-to {
  opacity: 0;
  transform: translateY(-4px);
  filter: blur(1px);
}

@media (prefers-reduced-motion: reduce) {
  .qp-route-enter-active,
  .qp-route-leave-active { transition: none; }
  .qp-route-enter-from,
  .qp-route-leave-to { transform: none; filter: none; }
}
</style>
